import React, { useState, useEffect, useMemo } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import Chip from "@mui/material/Chip";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Divider, TextField, MenuItem, Grid, Typography } from "@mui/material";
import Menu from "@mui/material/Menu";
import CheckIcon from "@mui/icons-material/Check";
import MessageIcon from "@mui/icons-material/Message";
import FlagIcon from "@mui/icons-material/Flag";
import EventIcon from "@mui/icons-material/Event";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import { Download, Search } from "@mui/icons-material";
import { useAuth } from "../../hooks/AuthProvider";
import { FilterModal, FilterModalItem } from "../FilterModal";
import { CalendarIcon } from "@mui/x-date-pickers";
import TableFilterButton from "../TableFilterButton";
import TableSearchInput from "../TableSearchInput";
import ExportButton from "../ExportButton";
import { ReactComponent as DropdownIcon } from "../../assets/images/icons/logs/dropdown-arrow.svg";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  RCS_BROADCAST_LOG_ENDPOINT,
  RCS_BROADCAST_LOG_EXPORT_ENDPOINT,
} from "../../config/urls";
import { ColorsConstants, searchBarWidth } from "../../cssUtils/constants";
import ButtonTwo from "../../common/dashboard-button";
import AppTable from "../../common/app-table";
import AppDropdown from "../../common/app-dropdown";
import {
  resolveLogPhoneSearchQueryForApi,
  normalizeCombinedSearchInput,
} from "../../utils/phoneSearch";
import { loadFilters, saveFilters, FILTER_NAMESPACES } from "../../utils/filterStorage";
import { formatWithTzLabel } from "../../utils/dateUtils";

const columns = [
  { id: "phone_number", label: "Phone Number", minWidth: 160 },
  { id: "datetime", label: "DateTime", minWidth: 180 },
  { id: "status", label: "Status", minWidth: 120 },
  { id: "message_type", label: "Message Type", minWidth: 160 },
  { id: "template", label: "Template", minWidth: 180 },
  { id: "description", label: "Description", minWidth: 300 },
  { id: "option", label: "Option", minWidth: 140 },
  { id: "campaign", label: "Campaign", minWidth: 200 },
  { id: "created_by", label: "Created By", minWidth: 180 },
];

export default function RCSBroadcastLogsTable({
  refreshKey = 0,
  dateInput = null,
  lastSynced = null,
  setLastSynced = () => {},
}) {
  const [rowCount, setRowCount] = useState(0);
  const [messageData, setmessageData] = useState([]);

  // 🟢 FIX: Initialize local pagination controller so it actually tracks the page!
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
    serverSidePage: 0,
  });

  const capitalizeFirstLetter = (str) => {
    return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";
  };

  const [savedFilters] = useState(() => {
    const s = loadFilters(FILTER_NAMESPACES.RCS_BROADCAST_LOGS, {
      searchQuery: '', statusFilter: '', optionFilter: '', fromDate: null, toDate: null,
    });
    return {
      ...s,
      fromDate: s.fromDate ? dayjs(s.fromDate) : null,
      toDate: s.toDate ? dayjs(s.toDate) : null,
    };
  });
  const [searchQuery, setSearchQuery] = useState(savedFilters.searchQuery);
  const [charCount, setCharCount] = useState(savedFilters.searchQuery.length);
  const [statusFilter, setStatusFilter] = useState(savedFilters.statusFilter);
  const [optionFilter, setOptionFilter] = useState(savedFilters.optionFilter);
  const [fromDate, setFromDate] = useState(savedFilters.fromDate);
  const [toDate, setToDate] = useState(savedFilters.toDate);
  const [dateError, setDateError] = useState(null);
  const { getValidToken } = useAuth();

  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [appliedDateRange, setAppliedDateRange] = useState([savedFilters.fromDate, savedFilters.toDate]);

  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [optionAnchorEl, setOptionAnchorEl] = useState(null);

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [exportState, setExportState] = useState("default");
  const [sortState, setSortState] = useState([]);

  // Phone search: use log-specific resolver (+E.164 when applicable) for marketing-style log APIs
  const resolvedRcsBroadcastSearch = useMemo(
    () => resolveLogPhoneSearchQueryForApi(searchQuery),
    [searchQuery],
  );

  const sortedData = useMemo(() => {
    if (!sortState.length || !messageData.length) return messageData;
    return [...messageData].sort((a, b) => {
      for (const { id, direction } of sortState) {
        let aVal = a[id];
        let bVal = b[id];
        if (aVal == null && bVal == null) continue;
        if (aVal == null) return direction === "asc" ? 1 : -1;
        if (bVal == null) return direction === "asc" ? -1 : 1;
        if (typeof aVal === "string") { aVal = aVal.toLowerCase(); bVal = String(bVal).toLowerCase(); }
        if (aVal < bVal) return direction === "asc" ? -1 : 1;
        if (aVal > bVal) return direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [messageData, sortState]);

  useEffect(() => {
    saveFilters(FILTER_NAMESPACES.RCS_BROADCAST_LOGS, {
      searchQuery,
      statusFilter,
      optionFilter,
      fromDate: fromDate ? dayjs(fromDate).toISOString() : null,
      toDate: toDate ? dayjs(toDate).toISOString() : null,
    });
  }, [searchQuery, statusFilter, optionFilter, fromDate, toDate]);

  const fetchData = async () => {
    if (resolvedRcsBroadcastSearch.blockFetch) {
      setmessageData([]);
      setRowCount(0);
      setLastSynced(null);
      return;
    }
    let searchDateURL = "";
    if (dateInput) {
      let dateString = dayjs(dateInput).format("YYYY-MM-DD");
      searchDateURL = `&search_date=${dateString}`;
    }

    const queryParams = new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: (controller.serverSidePage * controller.rowsPerPage).toString(),
      search_query: resolvedRcsBroadcastSearch.apiSearchQuery,
      status_filter: statusFilter,
      option_filter: optionFilter,
      from_date: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : "",
      to_date: toDate ? dayjs(toDate).format("YYYY-MM-DD") : "",
    });

    const tenant = localStorage.getItem("tenant");
    const token = await getValidToken();

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const response = await fetch(
        `${RCS_BROADCAST_LOG_ENDPOINT}?${queryParams}${searchDateURL}`,
        { headers },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const jsonData = await response.json();

      setmessageData(jsonData.results);
      setRowCount(jsonData.count);
      setLastSynced(
        jsonData.results.length > 0
          ? formatWithTzLabel(
              jsonData.results[0].created_at,
              "MMMM D, YYYY, h:mm:ss A",
            )
          : null,
      );
    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  const handleExport = async () => {
    const exportSearch = resolveLogPhoneSearchQueryForApi(searchQuery);
    if (exportSearch.blockFetch) {
      setExportState("failed");
      return;
    }
    try {
      setExportState("loading");
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const queryParams = new URLSearchParams({
        search_query: exportSearch.apiSearchQuery,
        status_filter: statusFilter,
        option_filter: optionFilter,
        from_date: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : "",
        to_date: toDate ? dayjs(toDate).format("YYYY-MM-DD") : "",
      });

      const response = await fetch(
        `${RCS_BROADCAST_LOG_EXPORT_ENDPOINT}?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-Id": tenant,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to export data");
      }

      const csvText = await response.text();
      const rows = csvText.split("\n");
      const updatedCsv = rows.join("\n");
      const blob = new Blob([updatedCsv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      const today = new Date().toISOString().split("T")[0];
      a.download = `rcs-broadcast-log-data-${today}.csv`;
      document.body.appendChild(a);
      a.click();

      setExportState("success");
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting data:", error.message);
      setExportState("failed");
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    controller.serverSidePage,
    controller.rowsPerPage,
    refreshKey,
    dateInput,
    searchQuery,
    statusFilter,
    optionFilter,
    fromDate,
    toDate,
  ]);

  useEffect(() => {
    setAppliedDateRange([fromDate, toDate]);
  }, []);

  const handleDateClick = (event) => setDateAnchorEl(event.currentTarget);

  const handleDateRangeApply = () => {
    setFromDate(appliedDateRange[0]);
    setToDate(appliedDateRange[1]);
    setDateAnchorEl(null);
    setController((prev) => ({ ...prev, page: 0, serverSidePage: 0 })); // 🟢 FIX
  };

  const handleDateRangeCancel = () => {
    setAppliedDateRange([fromDate, toDate]);
    setDateAnchorEl(null);
  };

  const updateSearchValue = (value) => {
    const normalized = normalizeCombinedSearchInput(value);
    if (normalized.length > 50) return;
    setSearchQuery(normalized);
    setCharCount(normalized.length);
    setController((prev) => ({ ...prev, page: 0, serverSidePage: 0 })); // 🟢 FIX
  };

  const handleOptionClick = (event) => setOptionAnchorEl(event.currentTarget);

  const handleOptionSelect = (option) => {
    const newValue = optionFilter === option ? "" : option;
    setOptionFilter(newValue);
    setOptionAnchorEl(null);
    setController((prev) => ({ ...prev, page: 0, serverSidePage: 0 })); // 🟢 FIX
  };

  const handleStatusClick = (event) => setStatusAnchorEl(event.currentTarget);

  const handleStatusSelect = (status) => {
    const newValue = statusFilter === status ? "" : status;
    setStatusFilter(newValue);
    setStatusAnchorEl(null);
    setController((prev) => ({ ...prev, page: 0, serverSidePage: 0 })); // 🟢 FIX
  };

  const handlePageChange = (event, newPage) => {
    setController((prev) => ({
      ...prev,
      page: newPage,
      serverSidePage: newPage,
    }));
  };

  // 🟢 FIX: Resolved broken JS syntax here
  const handleChangeRowsPerPage = (event) => {
    setController((prev) => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 20),
      page: 0,
      serverSidePage: 0,
    }));
  };

  const handleResetButtonClick = () => {
    setSearchQuery("");
    setStatusFilter("");
    setOptionFilter("");
    setAppliedDateRange([null, null]);
    setFromDate(null);
    setToDate(null);
    setSortState([]);
    setController((prev) => ({ ...prev, page: 0, serverSidePage: 0 }));
  };

  const handleSortChange = (columnId) => {
    setSortState((prev) => {
      const existing = prev.find((s) => s.id === columnId);
      if (!existing) return [...prev, { id: columnId, direction: "asc" }];
      if (existing.direction === "asc") return prev.map((s) => s.id === columnId ? { ...s, direction: "desc" } : s);
      return prev.filter((s) => s.id !== columnId);
    });
  };

  const getTotalActiveFilters = () => {
    let count = 0;
    if (statusFilter) count++;
    if (optionFilter) count++;
    if (fromDate) count++;
    if (toDate) count++;
    return count;
  };

  const tableColumns = [
    { id: "receiver", label: "To", minWidth: 160, sortable: true },
    {
      id: "logged_at",
      label: "Date",
      minWidth: 180,
      sortable: true,
      render: (message) =>
        formatWithTzLabel(
          message.logged_at || message.created_at,
          "MMMM D, YYYY, h:mm A",
        ),
    },
    { id: "status", label: "Status", minWidth: 120, sortable: true },
    { id: "message_type", label: "Message Type", minWidth: 160, sortable: true },
    {
      id: "segments",
      label: "Segments",
      minWidth: 100,
      // null = not billed yet, or billed inside a conversation
      render: (message) => message.segments ?? "-",
    },
    {
      id: "template",
      label: "Template",
      minWidth: 200,
      render: (message) => message?.template?.template_name,
    },
    { id: "error_reason", label: "Error Reason", minWidth: 200 },
    { id: "option", label: "Option", minWidth: 120, sortable: true },
    { id: "broadcast_name", label: "Broadcast Name", minWidth: 180 },
    { id: "campaign_name", label: "Campaign", minWidth: 160, sortable: true },
    { id: "created_by", label: "Created By", minWidth: 160, sortable: true },
  ];

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        backgroundColor: "#F7F7F7",
        border: "none",
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExport}
          >
            Export
          </Button>
        </Box>
        <Divider />
        <Box sx={{ display: "flex", mb: 2, gap: 1 }}>
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={(e) => updateSearchValue(e.target.value)}
            charCount={charCount}
            placeholder="Search by phone no, broadcast or campaign"
            error={Boolean(resolvedRcsBroadcastSearch.blockFetch && resolvedRcsBroadcastSearch.phoneSearchError)}
            helperText={resolvedRcsBroadcastSearch.blockFetch ? resolvedRcsBroadcastSearch.phoneSearchError : undefined}
          />
          <TableFilterButton
            setFilterModalOpen={setFilterModalOpen}
            totalActiveFilters={getTotalActiveFilters()}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          p: 0,
          m: 1,
          mx: 0,
          backgroundColor: "#F7F7F7",
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            overflowX: "auto",
            pb: 1,
            "&::-webkit-scrollbar": { height: "8px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "4px",
            },
          }}
        >
          {/* Search Filter */}
          <Box sx={{ width: searchBarWidth }}>
            <TableSearchInput
              searchQuery={searchQuery}
              onSearchChange={(e) => updateSearchValue(e.target.value)}
              charCount={charCount}
              placeholder="Search by phone no, broadcast or campaign"
              // placeholder="Search"
              error={Boolean(resolvedRcsBroadcastSearch.blockFetch && resolvedRcsBroadcastSearch.phoneSearchError)}
              helperText={resolvedRcsBroadcastSearch.blockFetch ? resolvedRcsBroadcastSearch.phoneSearchError : undefined}
            />
          </Box>

          {/* Date Range Filter */}
          <AppDropdown
            endIcon={<DropdownIcon />}
            sx={{
              whiteSpace: "nowrap",
              "& .MuiChip-root": {
                maxWidth: fromDate && toDate ? "120px" : "0px",
                transition: "max-width 0.2s ease",
              },
            }}
            paperProps={{ maxWidth: "500px" }}
            menuContent={(closeMenu) => (
              <Box sx={{ p: 2 }}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="en-gb"
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Typography variant="subtitle2">
                      Select Log Date:
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <DatePicker
                        label="From"
                        value={appliedDateRange[0]}
                        onChange={(newValue) => {
                          setAppliedDateRange([newValue, appliedDateRange[1]]);
                          if (
                            appliedDateRange[1] &&
                            newValue &&
                            dayjs(newValue).isAfter(appliedDateRange[1])
                          )
                            setDateError(
                              "From Date must be less than or equal to To Date",
                            );
                          else setDateError(null);
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: {
                              width: "200px",
                              "& .MuiInputBase-root": { height: "40px" },
                            },
                          },
                        }}
                      />
                      <DatePicker
                        label="To"
                        value={appliedDateRange[1]}
                        onChange={(newValue) => {
                          setAppliedDateRange([appliedDateRange[0], newValue]);
                          if (
                            appliedDateRange[0] &&
                            newValue &&
                            dayjs(newValue).isBefore(appliedDateRange[0])
                          )
                            setDateError(
                              "To Date must be greater than or equal to From Date",
                            );
                          else setDateError(null);
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: {
                              width: "200px",
                              "& .MuiInputBase-root": { height: "40px" },
                            },
                          },
                        }}
                      />
                    </Box>
                    {dateError && (
                      <Typography variant="caption" color="error">
                        {dateError}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                      }}
                    >
                      <Button
                        onClick={() => {
                          handleDateRangeCancel();
                          closeMenu();
                        }}
                        variant="outlined"
                      >
                        CANCEL
                      </Button>
                      <Button
                        onClick={() => {
                          handleDateRangeApply();
                          closeMenu();
                        }}
                        variant="contained"
                        disabled={
                          !appliedDateRange[0] ||
                          !appliedDateRange[1] ||
                          Boolean(dateError)
                        }
                      >
                        APPLY
                      </Button>
                    </Box>
                  </Box>
                </LocalizationProvider>
              </Box>
            )}
          >
            Log Date
            {fromDate && toDate && (
              <Tooltip
                title={`${dayjs(fromDate).format("MM/DD/YYYY")} - ${dayjs(toDate).format("MM/DD/YYYY")}`}
                placement="top"
                arrow
              >
                <Chip
                  label={`${dayjs(fromDate).format("MM/DD/YYYY")} - ${dayjs(toDate).format("MM/DD/YYYY")}`}
                  size="small"
                  onDelete={() => {
                    setFromDate(null);
                    setToDate(null);
                  }}
                  sx={{
                    ml: 1,
                    maxWidth: { sm: "80px", md: "200px" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                />
              </Tooltip>
            )}
          </AppDropdown>

          {/* Options Filter */}
          <AppDropdown
            endIcon={<DropdownIcon />}
            selectedValue={optionFilter}
            onMenuSelect={(value) => handleOptionSelect(value)}
            menuItems={[
              { label: "single", value: "single" },
              { label: "segment", value: "segment" },
              { label: "csv", value: "csv" },
            ]}
            sx={{ whiteSpace: "nowrap" }}
          >
            Options
            {optionFilter && (
              <Tooltip title={optionFilter} placement="top" arrow>
                <Chip
                  label={optionFilter}
                  size="small"
                  onDelete={(e) => {
                    e.stopPropagation();
                    handleOptionSelect(optionFilter);
                  }}
                  sx={{
                    ml: 1,
                    maxWidth: "120px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                />
              </Tooltip>
            )}
          </AppDropdown>

          {/* Status Filter */}
          <AppDropdown
            endIcon={<DropdownIcon />}
            menuItems={[
              { label: "Read", value: "read" },
              { label: "Delivered", value: "delivered" },
              { label: "Sent", value: "sent" },
              { label: "Enqueued", value: "enqueued" },
              { label: "Failed", value: "failed" },
            ]}
            selectedValue={statusFilter}
            onMenuSelect={(value) => handleStatusSelect(value)}
          >
            Status
            {statusFilter && (
              <Tooltip title={statusFilter} placement="top" arrow>
                <Chip
                  label={statusFilter}
                  size="small"
                  onDelete={() => handleStatusSelect(statusFilter)}
                  sx={{
                    ml: 1,
                    maxWidth: "120px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                />
              </Tooltip>
            )}
          </AppDropdown>

          {/* Reset Button */}
          <ButtonTwo
            startIcon={<RefreshIcon />}
            variant="outlined"
            onClick={handleResetButtonClick}
            sx={{
              height: "40px",
              borderRadius: "8px",
              textTransform: "capitalize",
              border: `1px solid ${ColorsConstants.primaryBackgroundColor}`,
              color: ColorsConstants.primaryBackgroundColor,
            }}
          >
            Reset
          </ButtonTwo>

          {/* Export Button */}
          <Box>
            <ExportButton
              handleExport={handleExport}
              state={exportState}
              setState={setExportState}
            />
          </Box>
        </Box>
      </Box>

      <AppTable
        columns={tableColumns}
        rows={sortedData}
        rowKey={(message, index) => index}
        emptyMessage="No match found"
        maxHeight={500}
        containerSx={{
          scrollbarWidth: "thin",
          scrollbarColor: "#C5CEDB #F3F6FB",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#F3F6FB", borderRadius: "8px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#C5CEDB", borderRadius: "8px" },
          "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A8B4C7" },
        }}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sortState={sortState}
        onSortChange={handleSortChange}
      />

      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onReset={handleResetButtonClick}
      >
        <FilterModalItem
          name="Options"
          data={["single", "segment", "csv"]}
          value={optionFilter}
          onChange={(e) => handleOptionSelect(e.target.value)}
          icon={<MessageIcon />}
        />
        <FilterModalItem
          name="Status"
          data={["read", "sent", "delivered", "enqueued", "failed"]}
          value={statusFilter}
          onChange={(e) => handleStatusSelect(e.target.value)}
          icon={<FlagIcon />}
        />
        <FilterModalItem
          name="From Date"
          type="date"
          value={appliedDateRange[0]}
          onChange={(newValue) => {
            setAppliedDateRange([newValue, appliedDateRange[1]]);
            if (
              appliedDateRange[1] &&
              newValue &&
              dayjs(newValue).isAfter(appliedDateRange[1])
            )
              setDateError("From Date must be less than or equal to To Date");
            else {
              setDateError(null);
              handleDateRangeApply();
            }
          }}
          icon={<CalendarIcon />}
        />
        <FilterModalItem
          name="To Date"
          type="date"
          value={appliedDateRange[1]}
          onChange={(newValue) => {
            setAppliedDateRange([appliedDateRange[0], newValue]);
            if (
              appliedDateRange[0] &&
              newValue &&
              dayjs(newValue).isBefore(appliedDateRange[0])
            )
              setDateError(
                "To Date must be greater than or equal to From Date",
              );
            else {
              setDateError(null);
              handleDateRangeApply();
            }
          }}
          icon={<CalendarIcon />}
        />
      </FilterModal>
    </Paper>
  );
}
